import { useState, useEffect } from 'react';
import type { RiskLevel } from '../types/checkin';

/**
 * Hook to detect and manage crisis overlay display
 * Automatically shows overlay for HIGH or CRISIS risk levels
 *
 * @param riskLevel - The risk level from AI analysis
 * @returns Object with overlay state and close handler
 *
 * @example
 * const { showOverlay, closeOverlay } = useCrisisDetection(analysis.riskLevel);
 *
 * return (
 *   <>
 *     <YourComponent />
 *     <CrisisOverlay
 *       riskLevel={analysis.riskLevel}
 *       isOpen={showOverlay}
 *       onClose={closeOverlay}
 *     />
 *   </>
 * );
 */
export function useCrisisDetection(riskLevel: RiskLevel) {
  const [showOverlay, setShowOverlay] = useState(false);

  // Auto-show overlay when risk level is HIGH or CRISIS
  useEffect(() => {
    if (riskLevel === 'high' || riskLevel === 'crisis') {
      setShowOverlay(true);
    }
  }, [riskLevel]);

  const closeOverlay = () => {
    setShowOverlay(false);
  };

  return {
    showOverlay,
    closeOverlay,
    riskLevel,
  };
}

export default useCrisisDetection;
